import { computed, Directive, ElementRef, HostListener, inject, input } from '@angular/core';

import { supportsNativeDisabled } from '../../../core/dom/natively-disableable-tags';

// Visual hierarchy of the button. `tertiary`, `link-color`, and `link-gray`
// are only valid for branding `universal` (the design system does not define branded variants).
export type IrdButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'link-color' | 'link-gray';

// Branding axis from the IR Design Foundation: `branded` follows the active brand palette,
// `universal` uses the brand-agnostic action palette.
export type IrdButtonBranding = 'universal' | 'branded';

export type IrdButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

@Directive({
  selector: '[irdButton]',
  standalone: true,
  host: {
    role: 'button',
    '[attr.aria-disabled]': 'disabled()',
    '[attr.aria-busy]': 'loading()',
    '[attr.tabindex]': 'disabled() ? -1 : 0',
    '[class]': '"button ird-button ird-button--" + branding() + "-" + variant() + " ird-button--" + size()',
    '[class.ird-button--icon-only]': 'iconOnly()',
    '[class.ird-button--disabled]': 'disabled()',
    '[class.ird-button--loading]': 'loading()',
    '[attr.disabled]': 'nativeDisabled()',
  },
})
export class IrdButtonDirective {
  readonly disabled = input(false);
  readonly loading = input(false);
  readonly branding = input<IrdButtonBranding>('universal');
  readonly variant = input<IrdButtonVariant>('primary');
  readonly size = input<IrdButtonSize>('md');
  readonly iconOnly = input(false);

  // `[disabled]` on the host binds this directive input, never the DOM property, so the
  // native control has to be disabled through the attribute or it stays clickable.
  private readonly hasNativeDisabled = supportsNativeDisabled(inject<ElementRef<HTMLElement>>(ElementRef).nativeElement);

  protected readonly nativeDisabled = computed(() => (this.hasNativeDisabled && (this.disabled() || this.loading()) ? '' : null));

  @HostListener('keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    const el = event.currentTarget as HTMLElement;
    if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    // Hosts without a native `disabled` attribute (e.g. `<a irdButton routerLink>`) still
    // activate on Enter while focused, and `pointer-events: none` only blocks the mouse,
    // so the default action has to be cancelled explicitly.
    if (this.disabled() || this.loading()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    event.preventDefault();
    el.click();
  }
}
